import { useEffect, useMemo, useState } from "react";
import type { AiRankingPayload, NewsDigest, NewsItem, NewsTopic } from "@dashboard/shared";
import { api } from "@/lib/api";
import { AiCostPanel, AiCostTile } from "@/components/AiCostCard";
import { AiRankingPanel, AiRankingTile } from "@/components/AiRankingCard";
import { useOpenCard } from "@/lib/aiRanking";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { relativeTime } from "@/lib/utils";

interface TopicPalette {
  chip: string;
  stripe: string;
  dot: string;
}

const TOPIC_PALETTES: Record<string, TopicPalette> = {
  "ai-labs":  { chip: "bg-indigo-500/15 text-indigo-700 border-indigo-500/20", stripe: "bg-indigo-500/50", dot: "bg-indigo-500" },
  "ai-biz":   { chip: "bg-emerald-500/15 text-emerald-700 border-emerald-500/20", stripe: "bg-emerald-500/50", dot: "bg-emerald-500" },
  "big-tech": { chip: "bg-amber-500/15 text-amber-700 border-amber-500/20", stripe: "bg-amber-500/50", dot: "bg-amber-500" },
  "geo-iran": { chip: "bg-rose-500/15 text-rose-700 border-rose-500/20", stripe: "bg-rose-500/50", dot: "bg-rose-500" },
  "geo-ukr":  { chip: "bg-sky-500/15 text-sky-700 border-sky-500/20", stripe: "bg-sky-500/50", dot: "bg-sky-500" },
  "geo-tw":   { chip: "bg-violet-500/15 text-violet-700 border-violet-500/20", stripe: "bg-violet-500/50", dot: "bg-violet-500" },
};
const FALLBACK_PALETTE: TopicPalette = { chip: "bg-primary/15 text-primary border-primary/20", stripe: "bg-primary/40", dot: "bg-primary" };
const palette = (id: string): TopicPalette => TOPIC_PALETTES[id] ?? FALLBACK_PALETTE;

/** `aiRanking` laedt App (es treibt auch das Badge am News-Tab); `freshAiChanges` sind
 *  die Top-10-Aenderungen, die beim Oeffnen dieses Tabs noch ungesehen waren. */
export function NewsTab({
  aiRanking,
  freshAiChanges,
}: {
  aiRanking: AiRankingPayload | null;
  freshAiChanges: Set<string>;
}) {
  const [rankingOpen, toggleRanking] = useOpenCard("ranking");
  const [costOpen, toggleCost] = useOpenCard("cost");
  const [topics, setTopics] = useState<NewsTopic[]>([]);
  const [itemsByTopic, setItemsByTopic] = useState<Record<string, NewsItem[]>>({});
  const [digest, setDigest] = useState<NewsDigest | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = () => {
      api.newsDigest().then((d) => { if (!cancelled) setDigest(d); }).catch(() => {
        if (!cancelled) setDigest(null);
      });
      api
        .newsTopics()
        .then(async (r) => {
          if (cancelled) return;
          setTopics(r.topics);
          const results = await Promise.all(
            r.topics.map((t) =>
              api
                .newsItems(t.id)
                .then((x) => [t.id, x.items] as [string, NewsItem[]])
                .catch(() => [t.id, [] as NewsItem[]] as [string, NewsItem[]]),
            ),
          );
          if (cancelled) return;
          const map: Record<string, NewsItem[]> = {};
          for (const [id, items] of results) map[id] = items;
          setItemsByTopic(map);
          setLoaded(true);
        })
        .catch(() => { if (!cancelled) setLoaded(true); });
    };

    load();
    // Täglicher Refresh (24h) + zusätzlich bei Tab-Fokus.
    const id = setInterval(load, 24 * 60 * 60 * 1000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  const topicLabelById = useMemo(() => {
    const m: Record<string, string> = {};
    for (const t of topics) m[t.id] = t.label;
    return m;
  }, [topics]);

  const latestOverall = useMemo(() => {
    const all: NewsItem[] = [];
    for (const items of Object.values(itemsByTopic)) all.push(...items);
    return all
      .sort((a, b) => (a.publishedAt < b.publishedAt ? 1 : -1))
      .slice(0, 6);
  }, [itemsByTopic]);

  const topImageByTopic = useMemo(() => {
    const m: Record<string, string> = {};
    for (const [id, items] of Object.entries(itemsByTopic)) {
      const withImg = items.find((it) => it.imageUrl);
      if (withImg?.imageUrl) m[id] = withImg.imageUrl;
    }
    return m;
  }, [itemsByTopic]);

  const lastRefreshById = useMemo(() => {
    const m: Record<string, string | null> = {};
    for (const [id, items] of Object.entries(itemsByTopic)) {
      const latest = items.reduce<string | null>((acc, it) => {
        if (!acc || it.fetchedAt > acc) return it.fetchedAt;
        return acc;
      }, null);
      m[id] = latest;
    }
    return m;
  }, [itemsByTopic]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">News Pulse</h2>
        <div className="text-xs text-muted-foreground">
          Sources: Reuters, Bloomberg, FT, TechCrunch, The Verge via Google News RSS
        </div>
      </div>

      {/* Daily digest fast in voller Breite (je Satz eine Zeile), rechts zwei kleine
          KI-Kacheln; aufgeklappt oeffnen sie sich in voller Breite darunter. */}
      <div className="grid grid-cols-[minmax(0,1fr)_11rem] items-start gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Daily digest</CardTitle>
            <CardDescription>
              {digest
                ? `Generated ${relativeTime(digest.generatedAt)} · ${digest.model}`
                : "LLM summary of the last 24h across all topics"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {digest ? (
              digest.overall.length ? (
                <ol className="list-decimal space-y-1 pl-5 text-sm leading-relaxed">
                  {/* Je Satz genau eine Zeile (Operator 2026-09-29); ein ausnahmsweise zu
                      langer Satz endet mit "…", voll im Tooltip. */}
                  {digest.overall.map((s, i) => (
                    <li key={i}>
                      <span className="block truncate" title={s}>{s}</span>
                    </li>
                  ))}
                </ol>
              ) : (
                <div className="text-sm text-muted-foreground">Digest is empty.</div>
              )
            ) : (
              <div className="space-y-2">
                {Array.from({ length: 10 }).map((_, i) => (
                  <Skeleton key={i} className="h-4" />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="space-y-3">
          <AiRankingTile data={aiRanking} fresh={freshAiChanges} open={rankingOpen} onToggle={toggleRanking} />
          <AiCostTile data={aiRanking} open={costOpen} onToggle={toggleCost} />
        </div>
      </div>

      {(rankingOpen || costOpen) && (
        <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-2">
          {rankingOpen && (
            <div className={costOpen ? "" : "xl:col-span-2"}>
              <AiRankingPanel data={aiRanking} fresh={freshAiChanges} onClose={toggleRanking} wide={!costOpen} />
            </div>
          )}
          {costOpen && (
            <div className={rankingOpen ? "" : "xl:col-span-2"}>
              <AiCostPanel data={aiRanking} onClose={toggleCost} />
            </div>
          )}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Latest across topics</CardTitle>
          <CardDescription>6 newest items</CardDescription>
        </CardHeader>
        <CardContent>
          {loaded ? (
            latestOverall.length ? (
              <ul className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                {latestOverall.map((item) => (
                  <li key={item.id} className="rounded-md border border-border bg-white/60 p-3">
                    <div className="mb-1 flex items-center gap-2">
                      <Badge className={palette(item.topic).chip}>{topicLabelById[item.topic] ?? item.topic}</Badge>
                      <span className="text-xs text-muted-foreground">
                        {relativeTime(item.publishedAt)}
                      </span>
                    </div>
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium leading-snug hover:underline"
                    >
                      {item.title}
                    </a>
                    {item.source && (
                      <div className="mt-1 text-xs text-muted-foreground">{item.source}</div>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-sm text-muted-foreground">
                No news yet — run the ingest cron (or <code className="text-xs">POST /news-trigger</code>).
              </div>
            )
          ) : (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-20" />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
        {topics.map((t) => {
          const items = itemsByTopic[t.id] ?? [];
          const refresh = lastRefreshById[t.id];
          const topicSummary = digest?.perTopic?.[t.id] ?? [];
          const img = topImageByTopic[t.id];
          const p = palette(t.id);
          return (
            <Card key={t.id} className="overflow-hidden">
              <div className={`h-1 w-full ${p.stripe}`} />
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <span className={`inline-block h-2 w-2 rounded-full ${p.dot}`} />
                  {t.label}
                </CardTitle>
                <CardDescription>
                  {refresh ? `Refreshed ${relativeTime(refresh)}` : "—"}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {img && (
                  <img
                    src={img}
                    alt=""
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    className="h-32 w-full rounded-md border border-border object-cover"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display = "none";
                    }}
                  />
                )}
                {topicSummary.length > 0 && (
                  <div className="space-y-1 text-sm leading-relaxed text-muted-foreground">
                    {topicSummary.map((s, i) => (
                      <p key={i}>{s}</p>
                    ))}
                  </div>
                )}
                {items.length ? (
                  <ul className="space-y-3">
                    {items.slice(0, 8).map((item) => (
                      <li key={item.id}>
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block text-sm font-medium leading-snug hover:underline"
                        >
                          {item.title}
                        </a>
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          {item.source ?? "—"} · {relativeTime(item.publishedAt)}
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : loaded ? (
                  <div className="text-sm text-muted-foreground">No items.</div>
                ) : (
                  <div className="space-y-2">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <Skeleton key={i} className="h-10" />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
